import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import None from 'rangeConfig/Icons/None.svg?react';
import Title from 'ui/Typography/Title';
import { Product } from '@inter-ikea-storageone/range';
import { isMobile } from 'utils/platform';
import { NoneOption } from 'rangeConfig/@types';
import { useCore } from 'contexts/core';
import { useBehaviorTracker } from 'contexts/behaviors';

type Props = {
  onLayoutSelected: (layout: Product, index: number) => void;
  selectedLayout: string;
  suggestionsForSelectedFrame: Product[];
  noneOption?: NoneOption;
};

const setBorder = (selected: boolean, theme: any) =>
  selected ? `3px solid ${theme.colors.hyperlink}` : `1px solid ${theme.colors.grey[200]}`;

const ListItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100px;
  justify-content: flex-start;
  padding: ${({ theme }) => theme.space.xl} 0;
  margin: ${({ theme }) => (isMobile() ? theme.space.xl : `${theme.space.xs} 0`)};
  border-radius: 4px;
`;

const StyledButton = styled.button<{ selected: boolean }>`
  position: relative;
  cursor: pointer;
  width: 100%;
  border: ${props => setBorder(props.selected, props.theme)};
  padding: 0;
  background: transparent;
  display: flex;
  align-items: center;
  border-radius: 4px;
  justify-content: flex-start;
  outline-offset: 4px;
  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};
  }
`;

const Image = styled.img`
  width: 80px;
  height: 80px;
  margin: 5px;
`;

const StyledNoneIcon = styled(None)`
  width: 50px;
  height: 50px;
  margin: 20px;
`;

const StyledTitle = styled(Title)`
  line-height: 1rem;
`;

const SingleColumnProductList = ({
  noneOption,
  selectedLayout,
  suggestionsForSelectedFrame,
  onLayoutSelected
}: Props) => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();

  const onNoneOptionSelected = () => {
    if (!noneOption) return;
    behaviorTracker.trigger('removeProducts', null, noneOption.behaviors);
  };

  return (
    <>
      {noneOption && (
        <ListItem key="none-option">
          <StyledButton
            onClick={onNoneOptionSelected}
            selected={noneOption?.selected(coreApi)}
            aria-label={formatMessage({
              id: 'storageone.frontLayoutOpen'
            })}
          >
            <StyledNoneIcon />
            <StyledTitle textAlign="center" type={ThemeFontStyleTypeEnum.headingXSmall}>
              {formatMessage({
                id: 'storageone.frontLayoutOpen'
              })}
            </StyledTitle>
          </StyledButton>
        </ListItem>
      )}
      {suggestionsForSelectedFrame.map((layout, i) => (
        <ListItem data-testid={layout.coreProductId} key={layout.coreProductId}>
          <StyledButton
            onClick={() => onLayoutSelected(layout, i)}
            selected={selectedLayout === layout.coreProductId}
            aria-label={layout.name}
          >
            <Image src={layout.images[0]?.sources[0]?.src || ''} alt={layout.name} />
            <StyledTitle textAlign="center" type={ThemeFontStyleTypeEnum.headingXSmall}>
              {layout.name}
            </StyledTitle>
          </StyledButton>
        </ListItem>
      ))}
    </>
  );
};

export default SingleColumnProductList;
